import React, { useState, useMemo, useEffect } from "react";
import {
  CURRENT_USER_ID,
  CURRENT_USER,
  SCREENS,
  MEETING_STATUS,
  STATUS_LABELS,
  DURATION_OPTIONS,
  TIME_SLOTS,
  MEETINGS_TABS,
  EMPTY_FORM,
  USERS,
} from "../Constants/ScheduleMeetingConst";
import "../Styles/ScheduleMeetingStyle.css";

/* =========================================================================
   SCHEDULE MEETING — FRONTEND-ONLY FEATURE
   -------------------------------------------------------------------------
   Self-contained React feature. No backend, no APIs, no OAuth, no Google
   integration. Everything runs on local React state with mock data so the
   backend team can wire real endpoints in later without changing the shape
   of the UI. Data shapes (User / MeetingRequest / MeetingStatus) are kept
   close to what a real API would likely return.
   ========================================================================= */

/* ============================ UTILITY FUNCTIONS ============================ */

function getInitialMeetingRequests() {
  const today = new Date();
  const inDays = (n) => {
    const d = new Date(today);
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };

  return [
    {
      id: "req-1001",
      senderId: 1,
      receiverId: CURRENT_USER_ID,
      meetingTitle: "Quarterly Roadmap Review",
      date: inDays(3),
      startTime: "11:00",
      duration: 30,
      message: "Let's align on the roadmap ahead of the quarterly review.",
      status: MEETING_STATUS.PENDING,
      meetingUrl: null,
    },
    {
      id: "req-1002",
      senderId: 3,
      receiverId: CURRENT_USER_ID,
      meetingTitle: "Design System Walkthrough",
      date: inDays(5),
      startTime: "15:30",
      duration: 45,
      message: "Would love to walk you through the updated component library.",
      status: MEETING_STATUS.PENDING,
      meetingUrl: null,
    },
    {
      id: "req-1003",
      senderId: 6,
      receiverId: CURRENT_USER_ID,
      meetingTitle: "Infrastructure Sync",
      date: inDays(-6),
      startTime: "10:00",
      duration: 30,
      message: "Quick sync on the migration timeline.",
      status: MEETING_STATUS.COMPLETED,
      meetingUrl: "https://meet.google.com/example-meeting",
    },
    {
      id: "req-1004",
      senderId: 5,
      receiverId: CURRENT_USER_ID,
      meetingTitle: "Budget Planning",
      date: inDays(-2),
      startTime: "14:00",
      duration: 60,
      message: "Discuss next quarter's budget allocation.",
      status: MEETING_STATUS.REJECTED,
      meetingUrl: null,
    },
  ];
}

function getUserById(id) {
  if (id === CURRENT_USER_ID) return CURRENT_USER;
  return USERS.find((u) => u.id === id) || null;
}

function getInitials(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

/* First bookable date: tomorrow, skipping weekends so the prefilled value
   is always valid. */
function nextWeekdayIso() {
  const d = new Date();
  do {
    d.setDate(d.getDate() + 1);
  } while (d.getDay() === 0 || d.getDay() === 6);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isWeekendIso(dateStr) {
  if (!dateStr) return false;
  const [y, m, d] = dateStr.split("-").map(Number);
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6;
}

function isPastDate(dateStr) {
  if (!dateStr) return false;
  return dateStr < todayIso();
}

function formatDateLong(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatTime12(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

function addMinutes(timeStr, minutes) {
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m + Number(minutes);
  const newH = Math.floor((total % (24 * 60)) / 60);
  const newM = total % 60;
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
}

function formatTimeRange(startTime, duration) {
  if (!startTime || !duration) return "";
  const end = addMinutes(startTime, duration);
  return `${formatTime12(startTime)} – ${formatTime12(end)}`;
}

function generateId(prefix) {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

function validateMeetingForm(form) {
  const errors = {};
  if (!form.title || !form.title.trim()) {
    errors.title = "Meeting title is required.";
  }
  if (!form.date) {
    errors.date = "Please select a date.";
  } else if (isPastDate(form.date)) {
    errors.date = "Date cannot be in the past.";
  } else {
    const [y, m, d] = form.date.split("-").map(Number);
    const day = new Date(y, m - 1, d).getDay();
    if (day === 0 || day === 6) {
      errors.date = "Weekends are not available. Please pick a weekday.";
    }
  }
  if (!form.time) {
    errors.time = "Please select a time.";
  }
  if (!form.duration) {
    errors.duration = "Please select a duration.";
  }
  return errors;
}

/* Filter a meeting list by the search box; matches title, participant name,
   or status label. Returns the list unchanged when the query is empty. */
function applyMeetingsSearch(list, rawQuery) {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return list;
  return list.filter((r) => {
    const sender = getUserById(r.senderId);
    const receiver = getUserById(r.receiverId);
    const statusLabel = (STATUS_LABELS[r.status] || r.status).toLowerCase();
    return (
      r.meetingTitle.toLowerCase().includes(q) ||
      statusLabel.includes(q) ||
      (sender?.name || "").toLowerCase().includes(q) ||
      (receiver?.name || "").toLowerCase().includes(q)
    );
  });
}

/* ================================ COMPONENTS ================================ */

function Avatar({ name, size = "md" }) {
  return (
    <div className={`Meeting-sm-avatar Meeting-sm-avatar--${size}`} aria-hidden="true">
      {getInitials(name)}
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`Meeting-sm-badge Meeting-sm-badge--${status}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function EmptyState({ title, message }) {
  return (
    <div className="Meeting-sm-empty">
      <p className="Meeting-sm-empty__title">{title}</p>
      <p className="Meeting-sm-empty__message">{message}</p>
    </div>
  );
}

function TopNav({ current, onChange, pendingCount }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const tabs = [
    { key: SCREENS.CONTACTS, label: "Contacts" },
    { key: SCREENS.MEETINGS, label: "Meetings" },
  ];

  const selectTab = (key) => {
    onChange(key);
    setMenuOpen(false);
  };

  return (
    <nav className="Meeting-sm-nav" aria-label="Schedule meeting sections">
      <div className="Meeting-sm-nav__inner">
        <span className="Meeting-sm-nav__brand">Schedule Meeting</span>
        <button
          type="button"
          className={`Meeting-sm-nav__burger ${
            menuOpen ? "Meeting-sm-nav__burger--open" : ""
          }`}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            {menuOpen ? (
              <>
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </>
            ) : (
              <>
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </>
            )}
          </svg>
        </button>

        <div
          className={`Meeting-sm-nav__dropdown ${
            menuOpen ? "Meeting-sm-nav__dropdown--open" : ""
          }`}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`Meeting-sm-nav__dropdown-item ${
                current === tab.key ? "Meeting-sm-nav__dropdown-item--active" : ""
              }`}
              onClick={() => selectTab(tab.key)}
            >
              {tab.label}
              {tab.key === SCREENS.MEETINGS && pendingCount > 0 && (
                <span className="Meeting-sm-nav__count">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        <div className="Meeting-sm-nav__tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={current === tab.key}
              className={`Meeting-sm-nav__tab ${
                current === tab.key ? "Meeting-sm-nav__tab--active" : ""
              }`}
              onClick={() => onChange(tab.key)}
            >
              {tab.label}
              {tab.key === SCREENS.MEETINGS && pendingCount > 0 && (
                <span className="Meeting-sm-nav__count">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

function ContactCard({ user, onMessage, onSchedule }) {
  return (
    <article className="Meeting-sm-contact-card">
      <div className="Meeting-sm-contact-card__top">
        <Avatar name={user.name} size="lg" />
        <div className="Meeting-sm-contact-card__identity">
          <h3 className="Meeting-sm-contact-card__name">{user.name}</h3>
          <p className="Meeting-sm-contact-card__role">{user.designation}</p>
          <p className="Meeting-sm-contact-card__meta">
            {user.company} &middot; {user.location}
          </p>
        </div>
      </div>
      <p className="Meeting-sm-contact-card__desc">{user.description}</p>
      <div className="Meeting-sm-contact-card__actions">
        <button
          type="button"
          className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--block-mobile Meeting-sm-btn--schedule"
          onClick={() => onSchedule(user)}
        >
          <svg
            className="Meeting-sm-schedule-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <rect x="2" y="6" width="13" height="12" rx="2" fill="currentColor" />
            <path d="M16 10.5 22 6.5v11l-6-4z" fill="currentColor" />
          </svg>
          <span className="Meeting-sm-schedule-label">Schedule Meeting</span>
        </button>
      </div>
    </article>
  );
}

function FormField({ label, error, children, hint }) {
  return (
    <div className={`Meeting-sm-field ${error ? "Meeting-sm-field--error" : ""}`}>
      <label className="Meeting-sm-field__label">{label}</label>
      {children}
      {hint && !error && <p className="Meeting-sm-field__hint">{hint}</p>}
      {error && (
        <p className="Meeting-sm-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const DATEPICKER_DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DATEPICKER_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* Custom calendar so weekends can be greyed out and made unselectable —
   the native <input type="date"> popup cannot be styled or filtered. */
function MeetingDatePicker({ value, min, onChange }) {
  const [open, setOpen] = useState(false);
  const initial = value ? new Date(`${value}T00:00:00`) : new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const rootRef = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocMouseDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const minDate = min ? new Date(`${min}T00:00:00`) : null;
  const displayValue = value
    ? (() => {
        const [y, m, d] = value.split("-");
        return `${d}-${m}-${y}`;
      })()
    : "";

  const startWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [
    ...Array.from({ length: startWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const isDisabledDay = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    if (date.getDay() === 0 || date.getDay() === 6) return true; /* weekend */
    if (minDate && date.getTime() < minDate.getTime()) return true;
    return false;
  };

  const canGoPrev =
    !minDate ||
    viewYear > minDate.getFullYear() ||
    (viewYear === minDate.getFullYear() && viewMonth > minDate.getMonth());

  return (
    <div className="Meeting-sm-datepicker" ref={rootRef}>
      <button
        type="button"
        className={`Meeting-sm-input Meeting-sm-datepicker__input ${
          value ? "" : "Meeting-sm-datepicker__input--empty"
        }`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        {displayValue || "Select date"}
      </button>

      {open && (
        <div className="Meeting-sm-datepicker__popup">
          <div className="Meeting-sm-datepicker__head">
            <button
              type="button"
              className="Meeting-sm-datepicker__nav"
              aria-label="Previous month"
              disabled={!canGoPrev}
              onClick={() => {
                setViewMonth((m) => {
                  if (m === 0) {
                    setViewYear((y) => y - 1);
                    return 11;
                  }
                  return m - 1;
                });
              }}
            >
              &lsaquo;
            </button>
            <span className="Meeting-sm-datepicker__title">
              {DATEPICKER_MONTHS[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              className="Meeting-sm-datepicker__nav"
              aria-label="Next month"
              onClick={() => {
                setViewMonth((m) => {
                  if (m === 11) {
                    setViewYear((y) => y + 1);
                    return 0;
                  }
                  return m + 1;
                });
              }}
            >
              &rsaquo;
            </button>
          </div>

          <div className="Meeting-sm-datepicker__grid">
            {DATEPICKER_DOW.map((dow, i) => (
              <span
                key={dow}
                className={`Meeting-sm-datepicker__dow ${
                  i === 0 || i === 6 ? "Meeting-sm-datepicker__dow--weekend" : ""
                }`}
              >
                {dow}
              </span>
            ))}
            {cells.map((day, idx) =>
              day === null ? (
                <span key={`empty-${idx}`} />
              ) : (
                (() => {
                  const disabled = isDisabledDay(day);
                  const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const selected = value === iso;
                  return (
                    <button
                      key={iso}
                      type="button"
                      disabled={disabled}
                      className={`Meeting-sm-datepicker__day ${
                        selected ? "Meeting-sm-datepicker__day--selected" : ""
                      }`}
                      onClick={() => {
                        onChange(iso);
                        setOpen(false);
                      }}
                    >
                      {day}
                    </button>
                  );
                })()
              )
            )}
          </div>

          <p className="Meeting-sm-datepicker__hint">Saturdays and Sundays are unavailable</p>
        </div>
      )}
    </div>
  );
}

function ScheduleFormModal({ person, form, errors, onChange, onSubmit, onCancel }) {
  return (
    <div className="Meeting-sm-modal-overlay" role="dialog" aria-modal="true" aria-label="Schedule a meeting">
      <div className="Meeting-sm-modal Meeting-sm-modal--form">
        <button
          type="button"
          className="Meeting-sm-modal__close"
          aria-label="Close"
          onClick={onCancel}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
        <div className="Meeting-sm-modal__body">
          <div className="Meeting-sm-modal__header">
            <h2 className="Meeting-sm-modal__title">Schedule Meeting</h2>
          </div>

          <div className="Meeting-sm-selected-person">
            <Avatar name={person.name} size="md" />
            <div>
              <p className="Meeting-sm-selected-person__name">{person.name}</p>
              <p className="Meeting-sm-selected-person__role">
                {person.designation} &middot; {person.company}
              </p>
            </div>
          </div>

          <form
            className="Meeting-sm-form Meeting-sm-form--compact"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            noValidate
          >
          <FormField label="Meeting Title" error={errors.title}>
            <input
              type="text"
              className="Meeting-sm-input"
              value={form.title}
              placeholder="e.g. Project Discussion"
              onChange={(e) => onChange("title", e.target.value)}
            />
          </FormField>

          <div className="Meeting-sm-form__row">
            <FormField label="Date" error={errors.date}>
              <MeetingDatePicker
                value={form.date}
                min={tomorrowIso()}
                onChange={(v) => onChange("date", v)}
              />
            </FormField>

            <FormField label="Time" error={errors.time}>
              <select
                className="Meeting-sm-input Meeting-sm-select"
                value={form.time}
                onChange={(e) => onChange("time", e.target.value)}
              >
                <option value="">Select time</option>
                {TIME_SLOTS.map((slot) => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <FormField label="Duration" error={errors.duration}>
            <select
              className="Meeting-sm-input Meeting-sm-select"
              value={form.duration}
              onChange={(e) => onChange("duration", e.target.value)}
            >
              <option value="">Select duration</option>
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Message">
            <textarea
              className="Meeting-sm-input Meeting-sm-textarea"
              rows={4}
              maxLength={200}
              value={form.message}
              placeholder="What would you like to discuss?"
              onChange={(e) => onChange("message", e.target.value)}
            />
            <div className="Meeting-sm-char-counter">
              {form.message.length}/200
            </div>
          </FormField>

          <div className="Meeting-sm-modal__actions">
            <button type="submit" className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--block-mobile">
              Schedule
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function RequestSentModal({ info, onViewMeetings, onClose }) {
  return (
    <div className="Meeting-sm-modal-overlay" role="dialog" aria-modal="true" aria-label="Meeting request sent">
      <div className="Meeting-sm-modal Meeting-sm-modal--status">
        <button
          type="button"
          className="Meeting-sm-modal__close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
        <h2 className="Meeting-sm-modal__title">
          Your meeting request has been sent to {info.person.name}.
        </h2>
        <div className="Meeting-sm-status-details">
          <p className="Meeting-sm-status-details__date">{formatDateLong(info.date)}</p>
          <p className="Meeting-sm-status-details__time">
            {formatTimeRange(info.time, info.duration)}
          </p>
        </div>
        <div className="Meeting-sm-modal__actions">
          <button type="button" className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--block-mobile" onClick={onViewMeetings}>
            View My Meetings
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmedMeetingModal({ request, otherUser, onClose }) {
  return (
    <div className="Meeting-sm-modal-overlay" role="dialog" aria-modal="true" aria-label="Meeting confirmed">
      <div className="Meeting-sm-modal Meeting-sm-modal--status">
        <button
          type="button"
          className="Meeting-sm-modal__close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
        <p className="Meeting-sm-modal__eyebrow">Meeting Confirmed</p>
        <h2 className="Meeting-sm-modal__title">{request.meetingTitle}</h2>
        <p className="Meeting-sm-status-details__with">With: {otherUser.name}</p>
        <div className="Meeting-sm-status-details">
          <p className="Meeting-sm-status-details__date">{formatDateLong(request.date)}</p>
          <p className="Meeting-sm-status-details__time">
            {formatTimeRange(request.startTime, request.duration)}
          </p>
        </div>
        <div className="Meeting-sm-modal__actions">
          <a
            className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--block-mobile"
            href={request.meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Join
          </a>
        </div>
      </div>
    </div>
  );
}

function AcceptConfirmModal({ onCancel, onConfirm }) {
  return (
    <div className="Meeting-sm-modal-overlay" role="dialog" aria-modal="true" aria-label="Accept meeting request">
      <div className="Meeting-sm-modal Meeting-sm-modal--confirm">
        <button
          type="button"
          className="Meeting-sm-modal__close"
          aria-label="Close"
          onClick={onCancel}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        <div className="Meeting-sm-modal__header">
          <h2 className="Meeting-sm-modal__title">Accept Meeting Request?</h2>
          <p className="Meeting-sm-modal__subtitle">
            A confirmation will be sent to the sender and the meeting will be
            added to your schedule.
          </p>
        </div>

        <div className="Meeting-sm-modal__actions">
          <button
            type="button"
            className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--block-mobile"
            onClick={onConfirm}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectConfirmModal({ message, error, onChange, onCancel, onConfirm }) {
  return (
    <div className="Meeting-sm-modal-overlay" role="dialog" aria-modal="true" aria-label="Reject meeting request">
      <div className="Meeting-sm-modal Meeting-sm-modal--confirm">
        <button
          type="button"
          className="Meeting-sm-modal__close"
          aria-label="Close"
          onClick={onCancel}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        <div className="Meeting-sm-modal__header">
          <h2 className="Meeting-sm-modal__title">Reject Meeting Request?</h2>
          <p className="Meeting-sm-modal__subtitle">
            A message with your reason will be sent to the sender explaining why
            you rejected this meeting request.
          </p>
        </div>

        <form
          className="Meeting-sm-form"
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm();
          }}
          noValidate
        >
          <FormField label="Message" error={error}>
            <textarea
              className="Meeting-sm-input Meeting-sm-textarea"
              rows={3}
              maxLength={200}
              value={message}
              placeholder="Tell the sender why you are rejecting this request"
              onChange={(e) => onChange(e.target.value)}
            />
            <span className="Meeting-sm-char-counter">{message.length}/200</span>
          </FormField>

          <div className="Meeting-sm-modal__actions">
            <button
              type="submit"
              className="Meeting-sm-btn Meeting-sm-btn--danger Meeting-sm-btn--block-mobile"
              disabled={!message.trim()}
            >
              Reject
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RescheduleModal({ request, otherUser, form, errors, onChange, onSubmit, onCancel }) {
  return (
    <div className="Meeting-sm-modal-overlay" role="dialog" aria-modal="true" aria-label="Reschedule meeting">
      <div className="Meeting-sm-modal Meeting-sm-modal--form">
        <button
          type="button"
          className="Meeting-sm-modal__close"
          aria-label="Close"
          onClick={onCancel}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
        <div className="Meeting-sm-modal__body">
        <div className="Meeting-sm-modal__header">
          <h2 className="Meeting-sm-modal__title">Reschedule Meeting</h2>
          <p className="Meeting-sm-modal__subtitle">
            Propose a new time for &ldquo;{request.meetingTitle}&rdquo; with {otherUser.name}.
          </p>
        </div>

        <form
          className="Meeting-sm-form"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          noValidate
        >
          <div className="Meeting-sm-form__row">
            <FormField label="New Date" error={errors.date}>
              <MeetingDatePicker
                value={form.date}
                min={tomorrowIso()}
                onChange={(v) => onChange("date", v)}
              />
            </FormField>

            <FormField label="New Time" error={errors.time}>
              <select
                className="Meeting-sm-input Meeting-sm-select"
                value={form.time}
                onChange={(e) => onChange("time", e.target.value)}
              >
                <option value="">Select time</option>
                {TIME_SLOTS.map((slot) => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <FormField label="Duration" error={errors.duration}>
            <select
              className="Meeting-sm-input Meeting-sm-select"
              value={form.duration}
              onChange={(e) => onChange("duration", e.target.value)}
            >
              <option value="">Select duration</option>
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Reason for Reschedule"
            error={errors.message}
          >
            <textarea
              className="Meeting-sm-input Meeting-sm-textarea"
              rows={3}
              maxLength={200}
              value={form.message}
              placeholder="Tell them why you need a new time"
              onChange={(e) => onChange("message", e.target.value)}
            />
            <div className="Meeting-sm-char-counter">
              {form.message.length}/200
            </div>
          </FormField>

          <div className="Meeting-sm-modal__actions">
            <button type="submit" className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--block-mobile">
              Request
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function CancelConfirmModal({ reason, error, onChange, onCancel, onConfirm }) {
  return (
    <div className="Meeting-sm-modal-overlay" role="dialog" aria-modal="true" aria-label="Cancel meeting">
      <div className="Meeting-sm-modal Meeting-sm-modal--sm Meeting-sm-modal--confirm">
        <div className="Meeting-sm-modal__header">
          <h2 className="Meeting-sm-modal__title">Cancel Meeting</h2>
          <button type="button" className="Meeting-sm-modal__close" onClick={onCancel} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>

        <div className="Meeting-sm-modal__body">
          <p className="Meeting-sm-cancel-modal__text">
            Are you sure you want to cancel this meeting? Please provide a reason.
          </p>

          <FormField label="Reason for Cancellation" error={error}>
            <textarea
              className="Meeting-sm-input Meeting-sm-textarea"
              rows={3}
              maxLength={200}
              value={reason}
              placeholder="Tell them why you need to cancel"
              onChange={(e) => onChange(e.target.value)}
            />
            <div className="Meeting-sm-char-counter">
              {reason.length}/200
            </div>
          </FormField>
        </div>

        <div className="Meeting-sm-modal__actions">
          <button
            type="button"
            className={`Meeting-sm-btn Meeting-sm-btn--danger Meeting-sm-btn--block-mobile ${
              !reason.trim() ? "Meeting-sm-btn--disabled" : ""
            }`}
            disabled={!reason.trim()}
            onClick={onConfirm}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

function IncomingRequestCard({ request, onAccept, onReject, onReschedule, onJoin }) {
  const sender = getUserById(request.senderId);
  const canAct = request.status === MEETING_STATUS.PENDING;
  return (
    <article className="Meeting-sm-request-card">
      <div className="Meeting-sm-request-card__top">
        <Avatar name={sender.name} size="md" />
        <div className="Meeting-sm-request-card__identity">
          <p className="Meeting-sm-request-card__from">{sender.name}</p>
          <p className="Meeting-sm-request-card__role">{sender.designation}</p>
          <p className="Meeting-sm-request-card__company">
            {sender.company} &middot; {sender.location}
          </p>
        </div>
        <StatusBadge status={request.status} />
      </div>

      <h3 className="Meeting-sm-request-card__title">{request.meetingTitle}</h3>

      <div className="Meeting-sm-request-card__meta">
        <span>{formatDateLong(request.date)}</span>
        <span>{formatTimeRange(request.startTime, request.duration)}</span>
      </div>

      {request.message && (
        <p className="Meeting-sm-request-card__message">{request.message}</p>
      )}

      {canAct ? (
        <div className="Meeting-sm-request-card__actions">
          <button
            type="button"
            className="Meeting-sm-btn Meeting-sm-btn--secondary Meeting-sm-btn--small Meeting-sm-btn--block-mobile"
            onClick={() => onReject(request.id)}
          >
            Reject
          </button>
          <button
            type="button"
            className="Meeting-sm-btn Meeting-sm-btn--secondary Meeting-sm-btn--small Meeting-sm-btn--block-mobile"
            onClick={() => onReschedule(request.id)}
          >
            Reschedule
          </button>
          <button
            type="button"
            className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--small Meeting-sm-btn--block-mobile"
            onClick={() => onAccept(request.id)}
          >
            Accept
          </button>
        </div>
      ) : (
        request.status === MEETING_STATUS.CONFIRMED && (
          <div className="Meeting-sm-request-card__actions">
            <button
              type="button"
              className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--small Meeting-sm-btn--block-mobile"
              onClick={() => onJoin(request)}
            >
              Join
            </button>
          </div>
        )
      )}
    </article>
  );
}

function MeetingListItem({ request, direction, onOpenConfirmed, onCancel }) {
  const otherUser =
    direction === "outgoing"
      ? getUserById(request.receiverId)
      : getUserById(request.senderId);

  return (
    <article className="Meeting-sm-meeting-row">
      <div className="Meeting-sm-meeting-row__left">
        <Avatar name={otherUser.name} size="sm" />
        <div>
          <p className="Meeting-sm-meeting-row__title">{request.meetingTitle}</p>
          <p className="Meeting-sm-meeting-row__person">
            {direction === "outgoing" ? "To" : "With"} {otherUser.name}
          </p>
          <p className="Meeting-sm-meeting-row__role">{otherUser.designation}</p>
          <p className="Meeting-sm-meeting-row__company">
            {otherUser.company} &middot; {otherUser.location}
          </p>
        </div>
      </div>

      <div className="Meeting-sm-meeting-row__mid">
        <span className="Meeting-sm-meeting-row__date">{formatDateLong(request.date)}</span>
        <span className="Meeting-sm-meeting-row__time">
          {formatTimeRange(request.startTime, request.duration)}
        </span>
      </div>

      <div className="Meeting-sm-meeting-row__right">
        <StatusBadge status={request.status} />
        {request.status === MEETING_STATUS.CONFIRMED && (
          <button
            type="button"
            className="Meeting-sm-btn Meeting-sm-btn--primary Meeting-sm-btn--small Meeting-sm-btn--block-mobile"
            onClick={() => onOpenConfirmed(request)}
          >
            Join
          </button>
        )}
        {direction === "outgoing" && request.status === MEETING_STATUS.PENDING && onCancel && (
          <button
            type="button"
            className="Meeting-sm-cancel-btn"
            onClick={() => onCancel(request.id)}
            aria-label="Cancel request"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
          </button>
        )}
      </div>
    </article>
  );
}

/* ================================ MAIN COMPONENT ================================ */

export default function ScheduleMeeting() {
  /* -------------------------------- REACT STATE -------------------------------- */
  const [currentScreen, setCurrentScreen] = useState(SCREENS.CONTACTS);
  const [searchQuery, setSearchQuery] = useState("");
  const [meetingsSearchQuery, setMeetingsSearchQuery] = useState("");
  const [meetingsTab, setMeetingsTab] = useState("requests");
  const [requestDirection, setRequestDirection] = useState("incoming");
  const [pastDirection, setPastDirection] = useState("completed");

  const [selectedPerson, setSelectedPerson] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const [sentInfo, setSentInfo] = useState(null);

  const [meetingRequests, setMeetingRequests] = useState(getInitialMeetingRequests());

  const [acceptTargetId, setAcceptTargetId] = useState(null);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectMessage, setRejectMessage] = useState("");
  const [rejectError, setRejectError] = useState(null);

  const [rescheduleTargetId, setRescheduleTargetId] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState(EMPTY_FORM);
  const [rescheduleErrors, setRescheduleErrors] = useState({});

  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState(null);

  const [confirmedMeeting, setConfirmedMeeting] = useState(null);

  /* -------------------------------- DERIVED DATA -------------------------------- */
  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return USERS;
    return USERS.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.company.toLowerCase().includes(q) ||
        u.designation.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const incomingRequests = useMemo(
    () => meetingRequests.filter((r) => r.receiverId === CURRENT_USER_ID),
    [meetingRequests]
  );

  const pendingIncomingCount = useMemo(
    () =>
      incomingRequests.filter((r) => r.status === MEETING_STATUS.PENDING).length,
    [incomingRequests]
  );

  const upcomingMeetings = useMemo(
    () =>
      meetingRequests
        .filter(
          (r) => r.status === MEETING_STATUS.CONFIRMED && !isPastDate(r.date)
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [meetingRequests]
  );

  const requestMeetings = useMemo(
    () =>
      meetingRequests
        .filter(
          (r) =>
            r.receiverId === CURRENT_USER_ID &&
            (r.status === MEETING_STATUS.PENDING ||
              r.status === MEETING_STATUS.RESCHEDULE_REQUESTED)
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [meetingRequests]
  );

  const sentMeetings = useMemo(
    () =>
      meetingRequests
        .filter(
          (r) =>
            r.senderId === CURRENT_USER_ID &&
            (r.status === MEETING_STATUS.PENDING ||
              r.status === MEETING_STATUS.RESCHEDULE_REQUESTED)
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [meetingRequests]
  );

  const pastMeetings = useMemo(
    () =>
      meetingRequests
        .filter(
          (r) =>
            r.status === MEETING_STATUS.COMPLETED ||
            r.status === MEETING_STATUS.REJECTED ||
            r.status === MEETING_STATUS.CANCELLED ||
            (r.status === MEETING_STATUS.CONFIRMED && isPastDate(r.date))
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [meetingRequests]
  );

  /* Search stays scoped to the active tab: each list below is the tab's data
     filtered by the search box. */
  const searchedUpcomingMeetings = useMemo(
    () => applyMeetingsSearch(upcomingMeetings, meetingsSearchQuery),
    [upcomingMeetings, meetingsSearchQuery]
  );

  const searchedRequestMeetings = useMemo(
    () => applyMeetingsSearch(requestMeetings, meetingsSearchQuery),
    [requestMeetings, meetingsSearchQuery]
  );

  const searchedSentMeetings = useMemo(
    () => applyMeetingsSearch(sentMeetings, meetingsSearchQuery),
    [sentMeetings, meetingsSearchQuery]
  );

  const searchedPastMeetings = useMemo(
    () => applyMeetingsSearch(pastMeetings, meetingsSearchQuery),
    [pastMeetings, meetingsSearchQuery]
  );

  const completedPastMeetings = useMemo(
    () => searchedPastMeetings.filter((r) => r.status === MEETING_STATUS.COMPLETED),
    [searchedPastMeetings]
  );

  const rejectedPastMeetings = useMemo(
    () => searchedPastMeetings.filter((r) => r.status === MEETING_STATUS.REJECTED),
    [searchedPastMeetings]
  );

  const isMeetingsSearching = meetingsSearchQuery.trim().length > 0;

  const meetingsSearchCounts = useMemo(() => {
    const q = meetingsSearchQuery.trim().toLowerCase();
    const counts = {
      upcoming: 0,
      requests: 0,
      past: 0,
    };
    const matchesSearch = (r) => {
      if (!q) return true;
      const sender = getUserById(r.senderId);
      const receiver = getUserById(r.receiverId);
      return (
        r.meetingTitle.toLowerCase().includes(q) ||
        (sender?.name || "").toLowerCase().includes(q) ||
        (sender?.company || "").toLowerCase().includes(q) ||
        (sender?.designation || "").toLowerCase().includes(q) ||
        (receiver?.name || "").toLowerCase().includes(q) ||
        (receiver?.company || "").toLowerCase().includes(q) ||
        (receiver?.designation || "").toLowerCase().includes(q)
      );
    };
    meetingRequests.forEach((r) => {
      if (r.status === MEETING_STATUS.CONFIRMED && !isPastDate(r.date)) {
        if (matchesSearch(r)) counts.upcoming += 1;
      }
      if (
        r.receiverId === CURRENT_USER_ID &&
        (r.status === MEETING_STATUS.PENDING || r.status === MEETING_STATUS.RESCHEDULE_REQUESTED)
      ) {
        if (matchesSearch(r)) counts.requests += 1;
      }
      if (
        r.status === MEETING_STATUS.COMPLETED ||
        r.status === MEETING_STATUS.REJECTED ||
        r.status === MEETING_STATUS.CANCELLED ||
        (r.status === MEETING_STATUS.CONFIRMED && isPastDate(r.date))
      ) {
        if (matchesSearch(r)) counts.past += 1;
      }
    });
    return counts;
  }, [meetingRequests, meetingsSearchQuery]);

  /* Auto-activate the first tab that has results for the current search.
     Depends only on the query so manual tab switching is never fought. */
  useEffect(() => {
    if (!meetingsSearchQuery.trim()) return;
    if (meetingsSearchCounts[meetingsTab] > 0) return;
    const order = ["requests", "upcoming", "past"];
    const firstWithResults = order.find((s) => meetingsSearchCounts[s] > 0);
    if (firstWithResults) setMeetingsTab(firstWithResults);
  }, [meetingsSearchQuery]);

  const rejectTarget = meetingRequests.find((r) => r.id === rejectTargetId) || null;
  const acceptTarget = meetingRequests.find((r) => r.id === acceptTargetId) || null;
  const rescheduleTarget =
    meetingRequests.find((r) => r.id === rescheduleTargetId) || null;
  const cancelTarget = meetingRequests.find((r) => r.id === cancelTargetId) || null;

  /* -------------------------------- EVENT HANDLERS -------------------------------- */

  function handleMessage(user) {
    // Frontend-only placeholder — messaging is out of scope for this feature.
    window.alert && undefined;
  }

  function openScheduleForm(person) {
    setSelectedPerson(person);
    setFormData({ ...EMPTY_FORM, date: nextWeekdayIso() });
    setFormErrors({});
  }

  function closeScheduleForm() {
    setSelectedPerson(null);
    setFormErrors({});
  }

  function handleFormChange(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function submitSchedule() {
    const errors = validateMeetingForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const newRequest = {
      id: generateId("req"),
      senderId: CURRENT_USER_ID,
      receiverId: selectedPerson.id,
      meetingTitle: formData.title.trim(),
      date: formData.date,
      startTime: formData.time,
      duration: Number(formData.duration),
      message: formData.message.trim(),
      status: MEETING_STATUS.PENDING,
      meetingUrl: null,
    };

    setMeetingRequests((prev) => [newRequest, ...prev]);
    setSentInfo({
      person: selectedPerson,
      date: formData.date,
      time: formData.time,
      duration: formData.duration,
    });
    setSelectedPerson(null);
  }

  function closeSentModalToContacts() {
    setSentInfo(null);
    setCurrentScreen(SCREENS.CONTACTS);
  }

  function closeSentModalToMeetings() {
    setSentInfo(null);
    setMeetingsTab("requests");
    setRequestDirection("outgoing");
    setCurrentScreen(SCREENS.MEETINGS);
  }

  function acceptRequest(id) {
    setMeetingRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status: MEETING_STATUS.CONFIRMED,
              meetingUrl: "https://meet.google.com/example-meeting",
            }
          : r
      )
    );
    const updated = meetingRequests.find((r) => r.id === id);
    if (updated) {
      setConfirmedMeeting({
        ...updated,
        status: MEETING_STATUS.CONFIRMED,
        meetingUrl: "https://meet.google.com/example-meeting",
      });
    }
  }

  function openAcceptModal(id) {
    setAcceptTargetId(id);
  }

  function closeAcceptModal() {
    setAcceptTargetId(null);
  }

  function confirmAccept() {
    const id = acceptTargetId;
    setAcceptTargetId(null);
    acceptRequest(id);
  }

  function openRejectModal(id) {
    setRejectTargetId(id);
    setRejectMessage("");
    setRejectError(null);
  }

  function closeRejectModal() {
    setRejectTargetId(null);
    setRejectError(null);
  }

  function handleRejectChange(value) {
    setRejectMessage(value);
    setRejectError(null);
  }

  function confirmReject() {
    const trimmed = rejectMessage.trim();
    if (!trimmed) {
      setRejectError("Please tell the sender why you are rejecting this request.");
      return;
    }
    setMeetingRequests((prev) =>
      prev.map((r) =>
        r.id === rejectTargetId
          ? { ...r, status: MEETING_STATUS.REJECTED, responseMessage: trimmed }
          : r
      )
    );
    setRejectTargetId(null);
  }

  function openRescheduleModal(id) {
    const target = meetingRequests.find((r) => r.id === id);
    if (!target) return;
    setRescheduleTargetId(id);
    setRescheduleForm({
      title: target.meetingTitle,
      date: target.date > tomorrowIso() && !isWeekendIso(target.date) ? target.date : nextWeekdayIso(),
      time: TIME_SLOTS.some((slot) => slot.value === target.startTime)
        ? target.startTime
        : "",
      duration: String(target.duration),
      message: "",
    });
    setRescheduleErrors({});
  }

  function closeRescheduleModal() {
    setRescheduleTargetId(null);
    setRescheduleErrors({});
  }

  function handleRescheduleChange(field, value) {
    setRescheduleForm((prev) => ({ ...prev, [field]: value }));
    setRescheduleErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function submitReschedule() {
    const errors = validateMeetingForm({ ...rescheduleForm, title: "placeholder" });
    if (!rescheduleForm.message.trim()) {
      errors.message = "Please add a short note about why you are rescheduling.";
    }
    if (Object.keys(errors).length > 0) {
      setRescheduleErrors(errors);
      return;
    }

    setMeetingRequests((prev) =>
      prev.map((r) =>
        r.id === rescheduleTargetId
          ? {
              ...r,
              date: rescheduleForm.date,
              startTime: rescheduleForm.time,
              duration: Number(rescheduleForm.duration),
              message: rescheduleForm.message.trim() || r.message,
              status: MEETING_STATUS.RESCHEDULE_REQUESTED,
            }
          : r
      )
    );
    setRescheduleTargetId(null);
  }

  function openCancelModal(id) {
    setCancelTargetId(id);
    setCancelReason("");
    setCancelError(null);
  }

  function closeCancelModal() {
    setCancelTargetId(null);
    setCancelError(null);
  }

  function handleCancelReasonChange(value) {
    setCancelReason(value);
    setCancelError(null);
  }

  function confirmCancel() {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelError("Please provide a reason for cancellation.");
      return;
    }
    setMeetingRequests((prev) =>
      prev.map((r) =>
        r.id === cancelTargetId
          ? { ...r, status: MEETING_STATUS.CANCELLED, cancelReason: trimmed }
          : r
      )
    );
    setCancelTargetId(null);
  }

  function closeConfirmedModal() {
    setConfirmedMeeting(null);
  }

  /* ---------------------------------- RENDER ---------------------------------- */

  return (
    <div className="Meeting-sm-root">
      <TopNav
        current={currentScreen}
        onChange={setCurrentScreen}
        pendingCount={pendingIncomingCount}
      />

      <main className="Meeting-sm-main">
        {currentScreen === SCREENS.CONTACTS && (
          <section className="Meeting-sm-container">
            <header className="Meeting-sm-page-header">
              <h1 className="Meeting-sm-page-header__title">Contacts</h1>
              <p className="Meeting-sm-page-header__subtitle">
                Find a colleague or partner and schedule time on their calendar.
              </p>
            </header>

            <div className="Meeting-sm-search">
              <div className="Meeting-sm-search__field">
                <input
                  id="Meeting-sm-search-input"
                  type="text"
                  className="Meeting-sm-input Meeting-sm-search__input"
                  placeholder="Search contacts"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <span className="Meeting-sm-search__icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                {searchQuery && (
                  <button
                    type="button"
                    className="Meeting-sm-search__clear"
                    aria-label="Clear search"
                    onClick={() => setSearchQuery("")}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    >
                      <line x1="6" y1="6" x2="18" y2="18" />
                      <line x1="18" y1="6" x2="6" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {filteredUsers.length === 0 ? (
              <EmptyState
                title="No contacts found"
                message="Try a different name, company, or designation."
              />
            ) : (
              <div className="Meeting-sm-contact-grid">
                {filteredUsers.map((user) => (
                  <ContactCard
                    key={user.id}
                    user={user}
                    onMessage={handleMessage}
                    onSchedule={openScheduleForm}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {currentScreen === SCREENS.MEETINGS && (
          <section className="Meeting-sm-container">
            <header className="Meeting-sm-page-header">
              <h1 className="Meeting-sm-page-header__title">Meetings</h1>
              <p className="Meeting-sm-page-header__subtitle">
                View and manage all your meetings, requests, and schedules.
              </p>
            </header>

            <div className="Meeting-sm-search">
              <div className="Meeting-sm-search__field">
                <input
                  id="Meeting-sm-meetings-search-input"
                  type="text"
                  className="Meeting-sm-input Meeting-sm-search__input"
                  placeholder="Search meetings"
                  value={meetingsSearchQuery}
                  onChange={(e) => setMeetingsSearchQuery(e.target.value)}
                />
                <span className="Meeting-sm-search__icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                {meetingsSearchQuery && (
                  <button
                    type="button"
                    className="Meeting-sm-search__clear"
                    aria-label="Clear search"
                    onClick={() => setMeetingsSearchQuery("")}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    >
                      <line x1="6" y1="6" x2="18" y2="18" />
                      <line x1="18" y1="6" x2="6" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="Meeting-sm-tabs" role="tablist" aria-label="Meetings filter">
              {MEETINGS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={meetingsTab === tab.key}
                  className={`Meeting-sm-tabs__tab ${
                    meetingsTab === tab.key ? "Meeting-sm-tabs__tab--active" : ""
                  }`}
                  onClick={() => setMeetingsTab(tab.key)}
                >
                  {tab.label}
                  {isMeetingsSearching && meetingsSearchCounts[tab.key] > 0 && (
                    <span className="Meeting-sm-tabs__count">
                      {meetingsSearchCounts[tab.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {meetingsTab === "upcoming" && (
              searchedUpcomingMeetings.length === 0 ? (
                <EmptyState
                  title={isMeetingsSearching ? "No matching meetings" : "No upcoming meetings"}
                  message={
                    isMeetingsSearching
                      ? "Nothing in Upcoming matches your search."
                      : "Confirmed meetings with future dates will show up here."
                  }
                />
              ) : (
                <div className="Meeting-sm-meeting-list">
                  {searchedUpcomingMeetings.map((r) => (
                    <MeetingListItem
                      key={r.id}
                      request={r}
                      direction={r.senderId === CURRENT_USER_ID ? "outgoing" : "incoming"}
                      onOpenConfirmed={setConfirmedMeeting}
                    />
                  ))}
                </div>
              )
            )}

            {meetingsTab === "requests" && (
              <div>
                <div className="Meeting-sm-toggle-container">
                  <div className="Meeting-sm-toggle-pill">
                    <button
                      type="button"
                      className={`Meeting-sm-toggle-pill__btn ${
                        requestDirection === "incoming" ? "Meeting-sm-toggle-pill__btn--active" : ""
                      }`}
                      onClick={() => setRequestDirection("incoming")}
                    >
                      Incoming
                    </button>
                    <button
                      type="button"
                      className={`Meeting-sm-toggle-pill__btn ${
                        requestDirection === "outgoing" ? "Meeting-sm-toggle-pill__btn--active" : ""
                      }`}
                      onClick={() => setRequestDirection("outgoing")}
                    >
                      Outgoing
                    </button>
                  </div>
                </div>

                {requestDirection === "incoming" ? (
                  searchedRequestMeetings.length === 0 ? (
                    <EmptyState
                      title={isMeetingsSearching ? "No matching requests" : "No incoming requests"}
                      message={
                        isMeetingsSearching
                          ? "Nothing in Incoming matches your search."
                          : "Received requests awaiting your response will show up here."
                      }
                    />
                  ) : (
                    <div className="Meeting-sm-request-list">
                      {searchedRequestMeetings.map((request) => (
                        <IncomingRequestCard
                          key={request.id}
                          request={request}
                          onAccept={openAcceptModal}
                          onReject={openRejectModal}
                          onReschedule={openRescheduleModal}
                          onJoin={setConfirmedMeeting}
                        />
                      ))}
                    </div>
                  )
                ) : (
                  searchedSentMeetings.length === 0 ? (
                    <EmptyState
                      title={isMeetingsSearching ? "No matching requests" : "No sent requests"}
                      message={
                        isMeetingsSearching
                          ? "Nothing in Outgoing matches your search."
                          : "Requests you have sent to others will show up here."
                      }
                    />
                  ) : (
                    <div className="Meeting-sm-meeting-list">
                      {searchedSentMeetings.map((r) => (
                        <MeetingListItem
                          key={r.id}
                          request={r}
                          direction="outgoing"
                          onOpenConfirmed={setConfirmedMeeting}
                          onCancel={openCancelModal}
                        />
                      ))}
                    </div>
                  )
                )}
              </div>
            )}

            {meetingsTab === "past" && (
              <div>
                <div className="Meeting-sm-toggle-container">
                  <div className="Meeting-sm-toggle-pill">
                    <button
                      type="button"
                      className={`Meeting-sm-toggle-pill__btn ${
                        pastDirection === "completed" ? "Meeting-sm-toggle-pill__btn--active" : ""
                      }`}
                      onClick={() => setPastDirection("completed")}
                    >
                      Completed
                    </button>
                    <button
                      type="button"
                      className={`Meeting-sm-toggle-pill__btn ${
                        pastDirection === "rejected" ? "Meeting-sm-toggle-pill__btn--active" : ""
                      }`}
                      onClick={() => setPastDirection("rejected")}
                    >
                      Rejected
                    </button>
                  </div>
                </div>

                {pastDirection === "completed" ? (
                  completedPastMeetings.length === 0 ? (
                    <EmptyState
                      title={isMeetingsSearching ? "No matching meetings" : "No completed meetings"}
                      message={
                        isMeetingsSearching
                          ? "Nothing in Completed matches your search."
                          : "Completed meetings will show up here."
                      }
                    />
                  ) : (
                    <div className="Meeting-sm-meeting-list">
                      {completedPastMeetings.map((r) => (
                        <MeetingListItem
                          key={r.id}
                          request={r}
                          direction={r.senderId === CURRENT_USER_ID ? "outgoing" : "incoming"}
                          onOpenConfirmed={setConfirmedMeeting}
                        />
                      ))}
                    </div>
                  )
                ) : (
                  rejectedPastMeetings.length === 0 ? (
                    <EmptyState
                      title={isMeetingsSearching ? "No matching meetings" : "No rejected meetings"}
                      message={
                        isMeetingsSearching
                          ? "Nothing in Rejected matches your search."
                          : "Rejected meetings will show up here."
                      }
                    />
                  ) : (
                    <div className="Meeting-sm-meeting-list">
                      {rejectedPastMeetings.map((r) => (
                        <MeetingListItem
                          key={r.id}
                          request={r}
                          direction={r.senderId === CURRENT_USER_ID ? "outgoing" : "incoming"}
                          onOpenConfirmed={setConfirmedMeeting}
                        />
                      ))}
                    </div>
                  )
                )}
              </div>
            )}
          </section>
        )}
      </main>

      {selectedPerson && (
        <ScheduleFormModal
          person={selectedPerson}
          form={formData}
          errors={formErrors}
          onChange={handleFormChange}
          onSubmit={submitSchedule}
          onCancel={closeScheduleForm}
        />
      )}

      {sentInfo && (
        <RequestSentModal
          info={sentInfo}
          onViewMeetings={closeSentModalToMeetings}
          onClose={closeSentModalToContacts}
        />
      )}

      {rejectTarget && (
        <RejectConfirmModal
          message={rejectMessage}
          error={rejectError}
          onChange={handleRejectChange}
          onCancel={closeRejectModal}
          onConfirm={confirmReject}
        />
      )}

      {acceptTarget && (
        <AcceptConfirmModal
          onCancel={closeAcceptModal}
          onConfirm={confirmAccept}
        />
      )}

      {rescheduleTarget && (
        <RescheduleModal
          request={rescheduleTarget}
          otherUser={getUserById(rescheduleTarget.senderId)}
          form={rescheduleForm}
          errors={rescheduleErrors}
          onChange={handleRescheduleChange}
          onSubmit={submitReschedule}
          onCancel={closeRescheduleModal}
        />
      )}

      {cancelTarget && (
        <CancelConfirmModal
          reason={cancelReason}
          error={cancelError}
          onChange={handleCancelReasonChange}
          onCancel={closeCancelModal}
          onConfirm={confirmCancel}
        />
      )}

      {confirmedMeeting && (
        <ConfirmedMeetingModal
          request={confirmedMeeting}
          otherUser={getUserById(
            confirmedMeeting.senderId === CURRENT_USER_ID
              ? confirmedMeeting.receiverId
              : confirmedMeeting.senderId
          )}
          onClose={closeConfirmedModal}
        />
      )}
    </div>
  );
}
